import React from 'react';
import PropTypes from 'prop-types';
import { Helmet } from 'react-helmet';

import { HeaderManager } from 'inside-out/managers/headerManager';
import { idToLink } from 'inside-out/managers/navManager';
import { FullWidthCTA } from 'inside-out/components/shared/fullWidthCTA';
import { fullAudiences } from 'inside-out/managers/audienceManager';

import { SoundCloud } from 'inside-out/components/shared/soundCloud';
import { SocialIcons } from 'inside-out/components/shared/socialIcons';
import AudienceIcon from 'inside-out/components/shared/icons/audienceIcon';
import BookmarkIcon from 'inside-out/components/shared/icons/bookmarkIcon';

import {
	innerWrapperStyle,
	detailWrapperStyle,
	metaAudiencesStyle,
  thumbnailStyle,
	titleStyle,
	mobileShareStyle,
	bookmarkMobileButtonStyle,
	bodyStyle,
	bookmarkButtonStyle,
	ctaStyle,
} from 'inside-out/page-styles/blog-and-podcast/detail';

import { detailSideBarLayout, contentSection, button, aqua } from 'inside-out/global-styles';

const audienceList = ['for-healthcare-professionals', 'for-myself', 'family-and-carers'];

// Gatsby requires a specific export format on Templates.
export default class PodcastDetail extends React.PureComponent {
	static propTypes = {
		data: PropTypes.shape({
			podcast: PropTypes.shape({
				edges: PropTypes.arrayOf(
					PropTypes.shape({
						node: PropTypes.shape({
              id: PropTypes.string.isRequired,
              audiences: PropTypes.arrayOf(PropTypes.string),
							title: PropTypes.string.isRequired,
							date: PropTypes.string.isRequired,
							extract: PropTypes.string,
							body: PropTypes.string.isRequired,
							fields: PropTypes.shape({
								thumbnail_image: PropTypes.shape({
									childImageSharp: PropTypes.shape({
										resize: PropTypes.shape({
											src: PropTypes.string.isRequired,
										}),
									}),
								}),
              }),
              attachments: PropTypes.arrayOf(
                PropTypes.shape({
                  file: PropTypes.string,
                  url: PropTypes.string,
                  link_text: PropTypes.string,
                  vimeo_url: PropTypes.string,
                  soundcloud_url: PropTypes.string,
                }),
              ),
						}),
					})
				),
			}),
		}),
	};

	componentDidMount = () => {
		HeaderManager.headerProperties = {};
	};

	convertVimeoURL = vimeo_url => {
		var replacement = '//player.vimeo.com/video/$1';
		return vimeo_url.replace(/(?:http?s?:\/\/)?(?:www\.)?(?:vimeo\.com)\/?(.+)/g, replacement);
	};

	render() {
		const { node } = this.props.data.podcast.edges[0];
    const audiences = fullAudiences(node.audiences);

    const hasVideoAttachment =
      node.attachments && node.attachments.find(attachment => attachment.vimeo_url);
    const bookmarkableContent = {
      to: idToLink(node.id),
      audiences,
      title: node.title,
      body: node.extract,
      date: node.date,
      hasVideo: hasVideoAttachment,
      background: node.fields && node.fields.thumbnail_image && node.fields.thumbnail_image.childImageSharp.resize.src
    };

		return (
			<section>
				<Helmet title={ node.title }>
			    <meta name="description" content={ node.extract }/>
			    <meta property="og:type" content="article"/>
			    <meta property="og:title" content={ node.title }/>
			    <meta property="og:description" content={ node.extract }/>
			    { node.fields && node.fields.thumbnail_image && node.fields.thumbnail_image.childImageSharp.resize.src && (
				    <meta property="og:image" content={ 'https://insideoutinstitute.org.au' + node.fields.thumbnail_image.childImageSharp.resize.src }/>
		    	) }
				</Helmet>

        <div className={innerWrapperStyle}>
          <div className={detailWrapperStyle}>
            <div className={detailSideBarLayout.leftSide}>
              <div className={contentSection('#fff')}>
                {!hasVideoAttachment &&
                  node.fields && (
                    <div className={thumbnailStyle}>
                      <img
                        src={node.fields.thumbnail_image.childImageSharp.resize.src}
                        alt=""
                      />
                    </div>
                  )}
                <div className={metaAudiencesStyle}>
                  {audiences.map(audience => (
                    <AudienceIcon key={audience.id} audience={audience} size={12} />
                  ))}
                </div>

                <h1 className={titleStyle}>{node.title}</h1>

                <div
                  className={bodyStyle}
                  dangerouslySetInnerHTML={{ __html: node.body }}
                />

                <div className={ctaStyle}>
                  {node.attachments && node.attachments.map((attachment, index) => {
                    const href = attachment.file || attachment.url;
                    if (attachment.soundcloud_url) {
                      return <SoundCloud key={index} url={attachment.soundcloud_url} />
                    } else if (attachment.vimeo_url) {
                      return (
                        <iframe
                          key={index}
                          src={this.convertVimeoURL(attachment.vimeo_url)}
                          width="100%"
                          frameBorder="0"
                          webkitallowfullscreen="true"
                          mozallowfullscreen="true"
                          allowFullScreen="true"/>
                      );
                    } else if (href) {
                      return (
                        <FullWidthCTA key={index} link={{ href }}>
                          {attachment.link_text || 'Read Full Article'}
                        </FullWidthCTA>
                      );
                    }
                  })}
                </div>
              </div>
            </div>

            <div className={mobileShareStyle}>
              <SocialIcons inline mode="share" {...node} />
              <div className={bookmarkMobileButtonStyle} data-bookmark="true">
                <BookmarkIcon width={27} bookmarkableContent={bookmarkableContent} bookmarkType="podcast"/>
              </div>
            </div>
            <div className={detailSideBarLayout.rightSide}>
              <div className={contentSection('#fff')}>
                <div className={bookmarkButtonStyle} data-bookmark="true">
                  <BookmarkIcon width={32} bookmarkableContent={bookmarkableContent} bookmarkType="podcast" />
                </div>
                <SocialIcons mode="share" {...node} />
              </div>
            </div>
          </div>
        </div>
			</section>
		);
	}
}

export const query = graphql`
	query PodcastsDetailQuery($id: String!) {
		podcast: allPodcastsJson(filter: { id: { eq: $id } }) {
			edges {
				node {
          ...podcast
					body
					attachments {
						file
						url
						link_text
            vimeo_url
            soundcloud_url
					}
				}
			}
		}
	}
`;



// WEBPACK FOOTER //
// ./src/templates/blog-and-podcast/podcast/detail/index.js